import useSendBiEvent from '../../../../hooks/useSendBiEvent';
import { useCrmOrgs } from '../../environments/useCrmOrgs';
import { useCallback, useState } from 'react';
import { FetchStatus } from '@server/fetch_status.type';
import { EnvironmentTypes } from '../../environments/EnvironmentTypeEnum';
import { useRunOnceWhenTruthy } from '../../../common/useRunOnceWhenTruthy';
import type {
  GET_STARTED_EVENTS,
  HOMEPAGE_EVENTS,
  SIGN_UP_EVENTS,
} from '../../../../services/events';
import { ACTIONS_EVENTS, UI_EVENTS } from '../../../../services/events';
import type { OrgAlreadyConnectedRetryData } from '../../environments/connectCrmSessionHelper';
import { CrmOrgConnectingError } from '../../environments/connectCrmSessionHelper';
import { ConnectCrmOrgStateEnum } from '../../environments/ConnectCrmOrgStateEnum';
import { getCrmOrgConnectionErrorData } from './getCrmOrgConnectionErrorData';
import { SweepNotificationVariant } from '../../../../reducers/notificationsReducer';
import { useSweepNotifications } from '../../../notifications/useSweepNotifications';
import { useDispatch, useSelector } from 'react-redux';
import { selectConnectionState, setConnectionState } from '../../../../reducers/connectOrgReducer';
import { selectCrmOrg } from '../../environments/environmentsReducer';
import useUserInfo from '../../../../hooks/useUserInfo';
import type { CrmOrg } from '../../../../types/CrmOrgs';
import OrgAlreadyConnectedConfirmDialog from './OrgAlreadyConnectedConfirmDialog';
import { useDefaultEnvId } from '../../environments/default-env-context/DefaultEnvContext';

export type getEventToSendUponSuccess = (
  isSandbox: boolean,
) => SIGN_UP_EVENTS | ACTIONS_EVENTS | GET_STARTED_EVENTS | HOMEPAGE_EVENTS;

interface AbstractConnectCrmOrgState {
  type: 'fetchStatus' | 'connectState';
}

interface ConnectCrmOrgStateFetchStatus extends AbstractConnectCrmOrgState {
  type: 'fetchStatus';
  fetchStatus: FetchStatus;
}

interface ConnectCrmOrgStateConnectState extends AbstractConnectCrmOrgState {
  type: 'connectState';
  connectState: ConnectCrmOrgStateEnum;
}

interface ConnectCrmOrgStateNotConnected extends ConnectCrmOrgStateConnectState {
  type: 'connectState';
  connectState: ConnectCrmOrgStateEnum.NotConnected | ConnectCrmOrgStateEnum.Connecting;
}

interface ConnectCrmOrgStateConnected extends ConnectCrmOrgStateConnectState {
  connectState: ConnectCrmOrgStateEnum.Connected;
}

interface ConnectCrmOrgStateError extends ConnectCrmOrgStateConnectState {
  connectState: ConnectCrmOrgStateEnum.Error;
  error: CrmOrgConnectingError;
}

export type ConnectCrmOrgState =
  | ConnectCrmOrgStateFetchStatus
  | ConnectCrmOrgStateNotConnected
  | ConnectCrmOrgStateConnected
  | ConnectCrmOrgStateError;

const useConnectToSf = ({
  getEventToSendUponSuccess,
  onFinish,
  forceProduction,
}: {
  getEventToSendUponSuccess?: getEventToSendUponSuccess;
  forceProduction: boolean;
  onFinish?: () => void;
}) => {
  const sendBiEvent = useSendBiEvent();
  const { addNotification } = useSweepNotifications();
  const currentCrmOrgId = useDefaultEnvId();
  const currentCrmOrg = useSelector(selectCrmOrg(currentCrmOrgId ?? ''));
  const { connectOrg, connectAnyway } = useCrmOrgs();
  const { updateHubspotContact } = useUserInfo();

  const dispatch = useDispatch();
  const connectState = useSelector(selectConnectionState);
  const [isSandbox, setIsSandbox] = useState(false);
  const [orgAlreadyConnectedData, setOrgAlreadyConnectedData] = useState<{
    crmOrg: CrmOrg;
    isSandbox: boolean;
    retryData: OrgAlreadyConnectedRetryData;
    callback?: () => void;
  } | null>(null);

  const isCurrentOrgFetchedOrFetching =
    currentCrmOrg &&
    (currentCrmOrg.fetchStatus === FetchStatus.Fetched ||
      currentCrmOrg.fetchStatus === FetchStatus.Fetching);

  const isOrgConnecting =
    connectState.type === 'connectState' &&
    connectState.connectState === ConnectCrmOrgStateEnum.Connecting;

  //reset the state
  useRunOnceWhenTruthy(
    () => {
      dispatch(
        setConnectionState({
          connectionState: {
            type: 'connectState',
            connectState: ConnectCrmOrgStateEnum.NotConnected,
          },
        }),
      );
    },
    !isCurrentOrgFetchedOrFetching || (!currentCrmOrg && !isOrgConnecting),
  );

  const setIsSandboxCb = useCallback((value: boolean) => {
    setIsSandbox(value);
  }, []);

  const onSuccess = useCallback(
    (crmOrg: CrmOrg, isSandboxValue: boolean) => {
      const defaultEvent = isSandboxValue
        ? ACTIONS_EVENTS.sfSandboxConnect
        : ACTIONS_EVENTS.sfProdConnect;
      const eventToSend = getEventToSendUponSuccess
        ? getEventToSendUponSuccess(isSandboxValue)
        : defaultEvent;
      sendBiEvent({
        name: eventToSend,
        props: {
          instanceUrl: crmOrg.instanceUrl,
          connectedUser: crmOrg.connectedUser,
        },
      });
      updateHubspotContact({
        last_connected_sf_url: crmOrg.instanceUrl,
        last_connected_sf_user: crmOrg.connectedUser,
      });
      dispatch(
        setConnectionState({
          connectionState: {
            type: 'connectState',
            connectState: ConnectCrmOrgStateEnum.Connected,
          },
        }),
      );
      onFinish && onFinish();
    },
    [getEventToSendUponSuccess, onFinish, dispatch, sendBiEvent, updateHubspotContact],
  );

  const onConnectStartHandler = useCallback(
    async ({
      isSandboxValue,
      forceMain,
      isDevelopment,
      withNotification,
      callback,
    }: {
      isSandboxValue: boolean;
      forceMain?: boolean;
      isDevelopment?: boolean;
      withNotification?: boolean;
      callback?: () => void;
    }) => {
      dispatch(
        setConnectionState({
          connectionState: {
            type: 'connectState',
            connectState: ConnectCrmOrgStateEnum.Connecting,
          },
        }),
      );

      const isSandboxValueToUse = isSandboxValue ?? isSandbox;
      const connectionType = isSandboxValueToUse
        ? EnvironmentTypes.Sandbox
        : isDevelopment
          ? EnvironmentTypes.Development
          : EnvironmentTypes.Production;
      sendBiEvent({ name: UI_EVENTS.connectToSfClick, props: { connectionType: connectionType } });

      const connectedResult = await connectOrg({
        type: connectionType,
        forceMain,
      });

      if (connectedResult.result === 'error') {
        if (
          connectedResult.error === CrmOrgConnectingError.ORG_ALREADY_CONNECTED_TO_SWEEP &&
          connectedResult.retryData &&
          connectedResult.crmOrg
        ) {
          setOrgAlreadyConnectedData({
            crmOrg: connectedResult.crmOrg,
            isSandbox: isSandboxValueToUse,
            retryData: connectedResult.retryData,
            callback,
          });
          dispatch(
            setConnectionState({
              connectionState: {
                type: 'connectState',
                connectState: ConnectCrmOrgStateEnum.NotConnected,
              },
            }),
          );
        } else {
          dispatch(
            setConnectionState({
              connectionState: {
                type: 'connectState',
                connectState: ConnectCrmOrgStateEnum.Error,
                error: connectedResult.error,
              },
            }),
          );

          const { description, title } = getCrmOrgConnectionErrorData({
            error: connectedResult.error,
          });

          if (withNotification) {
            addNotification({
              message: title,
              details: description.join(' '),
              variant: SweepNotificationVariant.Error,
              keepOpen: true,
            });
          }
        }
      } else {
        if (connectedResult.crmOrg) {
          onSuccess(connectedResult.crmOrg, isSandboxValueToUse);
        }
        callback && callback();
      }
      return connectedResult;
    },
    [isSandbox, sendBiEvent, dispatch, connectOrg, addNotification, onSuccess],
  );

  const onCancelOrgAlreadyConnected = useCallback(() => {
    setOrgAlreadyConnectedData(null);
  }, []);

  const onForceOrgAlreadyConnected = useCallback(async () => {
    if (!orgAlreadyConnectedData) return;

    const isSandboxValue = orgAlreadyConnectedData.isSandbox;
    const originalCallback = orgAlreadyConnectedData.callback;
    const crmOrg = orgAlreadyConnectedData.crmOrg;

    setOrgAlreadyConnectedData(null);

    dispatch(
      setConnectionState({
        connectionState: {
          type: 'connectState',
          connectState: ConnectCrmOrgStateEnum.Connecting,
        },
      }),
    );

    const result = await connectAnyway(crmOrg);

    if (result.result === 'success' && result.crmOrg) {
      onSuccess(result.crmOrg, isSandboxValue);
      originalCallback?.();
    } else {
      dispatch(
        setConnectionState({
          connectionState: {
            type: 'connectState',
            connectState: ConnectCrmOrgStateEnum.Error,
            error: CrmOrgConnectingError.ERROR_CONNECTING,
          },
        }),
      );
    }
  }, [orgAlreadyConnectedData, connectAnyway, dispatch, onSuccess]);

  //If we should connect Production, can skip the first step
  useRunOnceWhenTruthy(() => {
    onConnectStartHandler({ isSandboxValue: false });
  }, forceProduction);

  const orgAlreadyConnectedDialog = orgAlreadyConnectedData ? (
    <OrgAlreadyConnectedConfirmDialog
      isOpen={true}
      isSameAccount={orgAlreadyConnectedData.retryData.isSameAccount}
      onCancel={onCancelOrgAlreadyConnected}
      onForce={onForceOrgAlreadyConnected}
    />
  ) : null;

  return {
    connectState,
    isSandbox,
    setIsSandboxCb,
    onConnectStartHandler,
    orgAlreadyConnectedDialog,
  };
};

export default useConnectToSf;
